import React from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import { asyncPostData, asyncPutData } from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import { Avatar, Box, MenuItem, Stack, Typography } from "@mui/material";
import TextInput from "../../input/TextInput";
import SelectApiInput from "../../input/SelectApiInput";
import {
  formatDateDisplay,
  generateLinkImage,
} from "../../../../utils/helpers";

const schema = yup.object({
  content: yup.string().required("Vui lòng nhập nội dung bình luận"),
});

function FormComment({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const defaultData: { [key: string]: any } = {
    content: "",
    parent: null,
  };
  const originData = defaultValues
    ? {
        ...defaultData,
        ...defaultValues,
        parent: defaultValues?.replyId
          ? {
              _id: defaultValues?.replyId || "",
            }
          : null,
      }
    : null;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { parent, ...fields } = values;
    return {
      ...fields,
      replyId: parent?._id || "",
    };
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  return (
    <ModalForm
      formWidth="500px"
      formTitle="bình luận"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Stack gap={2}>
        <TextInput
          required
          multiline
          minRows={3}
          maxRows={5}
          label="Nội dung"
          labelWidth="25%"
          placeholder="Nhập nội dung"
          name="content"
          register={register}
          errorMessage={errors?.content?.message as string}
        />
        <Controller
          name="parent"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                labelWidth="25%"
                label="Bình luận cha"
                placeholder="Chọn bình luận cha"
                placeholderSearch="Tìm theo nội dung, tên người tạo"
                apiCode="socialcomment"
                searchFields={["content", "name_user_created"]}
                defaultCondition={{
                  kind: "post",
                  _id: !!defaultValues
                    ? { $ne: defaultValues?._id }
                    : undefined,
                }}
                conditionGetOrigin={value ? { _id: value?._id } : undefined}
                getOptionLabel={(option) => option?.content}
                checkEqual={(value, option) => value?._id === option?._id}
                renderOption={(option, { isEqual, key, onClick }) => {
                  return (
                    <MenuItem
                      key={key}
                      onClick={onClick}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        marginBottom: "5px",
                        backgroundColor: isEqual
                          ? "primary.500"
                          : "background.default",
                        borderRadius: "10px",
                      }}
                    >
                      <Box sx={{ pb: 1 }}>
                        <Typography
                          className="two-lines-ellipsis"
                          sx={{ whiteSpace: "wrap" }}
                        >
                          {option?.content}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          width: " 100%",
                          pt: 1,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Avatar
                            src={
                              option?.avatar
                                ? generateLinkImage(option?.avatar)
                                : ""
                            }
                            sx={{ width: "25px", height: "25px" }}
                          />
                          <Stack>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "primary.main",
                              }}
                            >
                              {option?.name_user_created}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontStyle: "italic",
                              }}
                            >
                              {formatDateDisplay(
                                option.date_created,
                                "HH:mm DD/MM/YYYY"
                              )}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  );
                }}
                value={value}
                onChange={onChange}
              />
            );
          }}
        />
      </Stack>
    </ModalForm>
  );
}

export default FormComment;
